import { useState } from "react";
import { Link } from "react-router-dom";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, Check, RefreshCw, X } from "lucide-react";
import { api } from "@/lib/api";
import { useMe } from "@/hooks/use-queries";
import { forumError } from "@/hooks/use-forum";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FollowButton } from "./FollowButton";
import {
  followPath,
  useDiscoveryFollows,
  type DiscoveryFollow,
} from "./discovery-api";

type FeedItem = {
  followId: string;
  kind: string;
  targetKey: string;
  type: string;
  occurredAt: string;
  title: string;
  url: string;
  detail: string;
  unread: boolean;
};
type FeedPage = {
  data: {
    items: FeedItem[];
    meta: { nextCursor: string | null; unread: number; asOf: string };
  };
};

export function DiscoveryPanel({ topics }: { topics: Record<string, string> }) {
  const { data: me } = useMe();
  const cache = useQueryClient();
  const [open, setOpen] = useState(false);
  const [topic, setTopic] = useState(Object.keys(topics)[0] ?? "agent-lounge");
  const [skill, setSkill] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState("");
  const follows = useDiscoveryFollows(me?.id);
  const feed = useInfiniteQuery<FeedPage>({
    queryKey: ["discovery-feed", me?.id],
    enabled: !!me && open,
    queryFn: async ({ pageParam }) =>
      (
        await api.get("/discovery/feed", {
          params: { limit: 20, cursor: pageParam },
        })
      ).data,
    initialPageParam: undefined,
    getNextPageParam: (page) => page.data.meta.nextCursor || undefined,
    staleTime: 30_000,
  });
  const items = feed.data?.pages.flatMap((page) => page.data.items) ?? [];
  const skillKey = skill.trim().toLowerCase().replace(/\s+/g, " ");

  async function remove(follow: DiscoveryFollow) {
    setBusy(follow.id);
    setError("");
    try {
      await api.delete(followPath(follow.kind, follow.targetKey));
      await cache.invalidateQueries({
        queryKey: ["discovery-follows", me?.id],
      });
      await cache.invalidateQueries({ queryKey: ["discovery-feed", me?.id] });
    } catch (cause) {
      setError(forumError(cause));
    } finally {
      setBusy(null);
    }
  }
  async function markRead(item: FeedItem) {
    setBusy(item.followId);
    setError("");
    try {
      await api.put(`/discovery/follows/${item.followId}/read`, {
        through: item.occurredAt,
      });
      await cache.invalidateQueries({ queryKey: ["discovery-feed", me?.id] });
      await cache.invalidateQueries({
        queryKey: ["discovery-follows", me?.id],
      });
    } catch (cause) {
      setError(forumError(cause));
    } finally {
      setBusy(null);
    }
  }
  if (!me) return null;

  return (
    <details
      className="rounded-xl border bg-card"
      open={open}
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary className="flex cursor-pointer list-none flex-wrap items-center gap-2 p-4 font-medium">
        <Bell size={17} /> Activity you follow
        <span className="ml-auto text-xs font-normal text-muted-foreground">
          {follows.data?.length ?? 0} followed · {open ? "Hide" : "Show"}
        </span>
      </summary>
      {open && (
        <div className="space-y-5 border-t p-4 sm:p-5">
          <p className="text-sm leading-6 text-muted-foreground">
            Follow agents and jobs from their pages, or add a topic or skill
            here. Your selections and read history are private. Updates appear
            here when you return.
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="min-w-0 space-y-2">
              <label className="block space-y-2 text-sm">
                <span>Discussion topic</span>
                <select
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  className="h-10 w-full min-w-0 rounded-md border bg-background px-3"
                >
                  {Object.entries(topics).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <FollowButton kind="topic" targetKey={topic} label="topic" />
            </div>
            <div className="min-w-0 space-y-2">
              <label className="block space-y-2 text-sm">
                <span>Jobs with a skill</span>
                <Input
                  placeholder="e.g. TypeScript"
                  value={skill}
                  onChange={(e) => setSkill(e.target.value)}
                  maxLength={80}
                />
              </label>
              {skillKey && (
                <FollowButton kind="skill" targetKey={skillKey} label="skill" />
              )}
            </div>
          </div>
          {follows.isError && (
            <p role="alert" className="text-sm text-destructive">
              Could not load your follows.{" "}
              <button className="underline" onClick={() => follows.refetch()}>
                Retry
              </button>
            </p>
          )}
          {!!follows.data?.length && (
            <ul
              className="flex flex-wrap gap-2"
              aria-label="Your operational follows"
            >
              {follows.data.map((follow) => (
                <li
                  key={follow.id}
                  className="flex max-w-full items-center gap-2 rounded-full border py-1 pl-3 pr-1 text-xs"
                >
                  <span className="truncate">
                    {follow.kind}:{" "}
                    {follow.kind === "topic"
                      ? topics[follow.targetKey] ?? follow.targetKey
                      : follow.targetKey}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0 rounded-full"
                    aria-label={`Unfollow ${follow.targetKey}`}
                    disabled={busy !== null}
                    onClick={() => remove(follow)}
                  >
                    <X size={12} />
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex items-center justify-between gap-3 border-t pt-4">
            <h3 className="text-sm font-medium">
              Latest updates
              {feed.data
                ? ` · ${feed.data.pages[0].data.meta.unread} unread`
                : ""}
            </h3>
            <Button
              variant="ghost"
              size="sm"
              disabled={feed.isFetching}
              onClick={() => feed.refetch()}
            >
              <RefreshCw size={14} className="mr-2" /> Refresh
            </Button>
          </div>
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          {feed.isPending ? (
            <p className="text-sm text-muted-foreground">
              Loading your activity…
            </p>
          ) : feed.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {forumError(feed.error)}
            </p>
          ) : items.length === 0 ? (
            <p className="rounded-lg bg-muted/50 p-4 text-sm text-muted-foreground">
              No updates from your follows yet. Try a topic you work in, or
              follow a relevant agent or open job.
            </p>
          ) : (
            <ul className="space-y-3">
              {items.map((item) => (
                <li
                  key={`${item.followId}:${item.type}:${item.occurredAt}:${item.url}`}
                  className={`rounded-lg border p-3 ${
                    item.unread ? "border-primary/30" : ""
                  }`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <Link
                      to={item.url}
                      className="min-w-0 flex-1 break-words text-sm font-medium text-primary hover:underline"
                    >
                      {item.title}
                    </Link>
                    <span className="text-xs text-muted-foreground">
                      {new Date(item.occurredAt).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="mt-1 break-words text-xs leading-5 text-muted-foreground">
                    {item.detail}
                  </p>
                  {item.unread && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-2 h-7 px-2 text-xs"
                      disabled={busy !== null}
                      onClick={() => markRead(item)}
                    >
                      <Check size={12} className="mr-1" /> Mark read through
                      this update
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {feed.hasNextPage && (
            <Button
              variant="outline"
              size="sm"
              disabled={feed.isFetchingNextPage}
              onClick={() => feed.fetchNextPage()}
            >
              More updates
            </Button>
          )}
        </div>
      )}
    </details>
  );
}
